/**
 * The seeded accounts, as data both the fixture and the sign-in screen can read.
 *
 * Locally the sign-in page lists these by calling `/__dev/accounts`, which reads
 * them out of the database and therefore cannot go stale. That endpoint needs a
 * schema-owner connection — listing every account across every firm is a
 * cross-tenant question by definition — and `loadConfig` refuses that pairing in
 * production, correctly.
 *
 * So the deployed demo needs a second source for the same list, and a
 * hand-maintained copy is exactly the thing the database-backed version was
 * written to avoid: the previous hardcoded list had drifted to four of seven
 * accounts, missing the manager and the second bookkeeper. This module is that
 * source, and `assertRosterMatchesSeed` in the seed fails the run if the two ever
 * disagree — so the drift is caught where it is introduced rather than noticed on
 * a demo call.
 *
 * It lives in @onecpa/shared because both the browser and the seed need it, and
 * it is plain data: no node builtins, no server packages, nothing that would
 * break the web bundle.
 */
export interface DemoMembership {
  orgName: string;
  orgSlug: string;
  orgStatus: "active" | "trialing" | "past_due" | "suspended";
  role: "owner" | "manager" | "staff" | "client_user";
  clientName: string | null;
}

export interface DemoAccount {
  email: string;
  fullName: string;
  memberships: DemoMembership[];
}

/** Every seeded account uses this. The demo holds nothing real, so it is published. */
export const DEMO_PASSWORD = "onecpa123";

const DK = { orgName: "משרד דניס קלוצקי, רואי חשבון", orgSlug: "dk-cpa", orgStatus: "active" } as const;
const LEVI = { orgName: "ש. לוי, יעוץ מס וראיית חשבון", orgSlug: "levi-tax", orgStatus: "trialing" } as const;
const BRENNER = { orgName: "נ. ברנר, ראיית חשבון", orgSlug: "brenner", orgStatus: "past_due" } as const;
const AMIT = { orgName: "עמית ושות׳, רואי חשבון", orgSlug: "amit-partners", orgStatus: "active" } as const;
const SHANI = { orgName: "שני הנהלת חשבונות", orgSlug: "shani-books", orgStatus: "active" } as const;
const NEGEV = { orgName: "רו״ח הנגב", orgSlug: "negev-cpa", orgStatus: "suspended" } as const;

export const DEMO_ACCOUNTS: DemoAccount[] = [
  {
    email: "owner@dkcpa.test",
    fullName: "דניס קלוצקי",
    // Two memberships on purpose: this is the account that gives the firm switcher
    // something to switch between.
    memberships: [
      { ...DK, role: "owner", clientName: null },
      { ...LEVI, role: "manager", clientName: null },
    ],
  },
  {
    email: "manager@dkcpa.test",
    fullName: "רונית לוי",
    memberships: [{ ...DK, role: "manager", clientName: null }],
  },
  {
    email: "staff@dkcpa.test",
    fullName: "מיכל ברק",
    memberships: [{ ...DK, role: "staff", clientName: null }],
  },
  {
    email: "staff2@dkcpa.test",
    fullName: "דנה שגב",
    memberships: [{ ...DK, role: "staff", clientName: null }],
  },
  {
    email: "client@dkcpa.test",
    fullName: "אבי אבני",
    memberships: [{ ...DK, role: "client_user", clientName: "אבני דרך בע״מ" }],
  },
  {
    email: "owner@levitax.test",
    fullName: "שמעון לוי",
    memberships: [{ ...LEVI, role: "owner", clientName: null }],
  },
  {
    email: "owner@newfirm.test",
    fullName: "נועה ברנר",
    memberships: [{ ...BRENNER, role: "owner", clientName: null }],
  },
  {
    email: "owner@amit.test",
    fullName: "עמית רוזן",
    memberships: [{ ...AMIT, role: "owner", clientName: null }],
  },
  {
    email: "manager@amit.test",
    fullName: "יעל אדרי",
    memberships: [{ ...AMIT, role: "manager", clientName: null }],
  },
  {
    email: "staff@amit.test",
    fullName: "אורי בן חיים",
    memberships: [{ ...AMIT, role: "staff", clientName: null }],
  },
  {
    email: "owner@shani.test",
    fullName: "שני מזרחי",
    memberships: [{ ...SHANI, role: "owner", clientName: null }],
  },
  {
    email: "owner@negev.test",
    fullName: "איתן הראל",
    memberships: [{ ...NEGEV, role: "owner", clientName: null }],
  },
];
